import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';

/**
 * usePitchbookAccess — mirrors the `useInboxAccess` pattern.
 *
 * Returns `{ hasAccess, isLoading }`. The fetch is scoped to admins only via
 * `enabled: Boolean(user?.is_admin)` so non-admins never make the request.
 *
 * The backend is the source of truth: if `GET /api/pitchbook/config` returns
 * 200, PitchBook is configured and the nav item should be visible. A 404 or
 * error means it is not configured, and `hasAccess` stays false.
 *
 * Used by ContextualSidebar to gate the PitchBook Sync nav item, mirroring how
 * `useInboxAccess` gates the Unified Inbox nav item.
 */
export function usePitchbookAccess() {
    const { user, getAuthHeader } = useAuth();

    const { isLoading, error } = useQuery({
        queryKey: ['pitchbook-config'],
        queryFn: () => apiFetch('/pitchbook/config', getAuthHeader()),
        enabled: Boolean(user?.is_admin),
        retry: false,
        staleTime: 5 * 60 * 1000, // 5 min — same as useInboxAccess
        refetchOnWindowFocus: false,
    });

    // 404 = feature not configured (not an error). Any other error also means
    // not configured. Only a successful 200 response means access is granted.
    return {
        isLoading,
        hasAccess: !error && Boolean(user?.is_admin),
    };
}
